import React, { useState, useEffect, useRef } from 'react';
import { trpc } from '@/trpc/client';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  MapPin,
  ArrowRight,
  Play,
  Square,
  Loader2,
  Signal,
  Terminal,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import { useSocket } from '@/hooks/useSocket';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

// Frontend-safe enum (mirrors backend values, no Prisma import)
export const BookingStatus = {
  ACCEPTED: 'ACCEPTED',
  PICKED_UP: 'PICKED_UP',
  IN_TRANSIT: 'IN_TRANSIT',
  AT_DELIVERY: 'AT_DELIVERY',
  DELIVERED: 'DELIVERED',
} as const;
export type BookingStatus = typeof BookingStatus[keyof typeof BookingStatus];

const ActiveJobCard = ({ booking }: { booking: any }) => {
  const utils = trpc.useUtils();
  const { setTrackingJobId, trackingJobId } = useAuthStore();
  const { socket, isConnected } = useSocket();

  const [isTracking, setIsTracking] = useState(false);
  const geolocationIntervalRef = useRef<number | null>(null);
  const wakeLockRef = useRef<any>(null); // Screen Wake Lock
  const offlineQueueRef = useRef<{ lat: number, lng: number, time: number }[]>([]); // Offline queue

  // Removed unsafe 'as any' cast - ensuring strict typing
  const updateStatusMutation = trpc.booking.updateBookingStatus.useMutation({
    onSuccess: (updatedBooking: any) => {
      toast.success('Job status updated!');
      utils.booking.getMyActiveJobs.invalidate();
      if (updatedBooking.status === BookingStatus.IN_TRANSIT) {
        startTracking();
      }
    },
    onError: (err: any) => toast.error(err.message),
  });

  const sendLocationMutation = trpc.location.transporterSendLocation.useMutation();

  useEffect(() => {
    setIsTracking(trackingJobId === booking.jobId);
  }, [trackingJobId, booking.jobId]);

  // Flush offline queue when reconnected
  useEffect(() => {
    if (isConnected && offlineQueueRef.current.length > 0) {
      const queue = [...offlineQueueRef.current];
      offlineQueueRef.current = []; // Clear early to prevent double send
      console.log(`🚀 Flushing ${queue.length} offline location points...`);

      queue.forEach(point => {
        sendLocationMutation.mutate({
          jobId: booking.jobId,
          latitude: point.lat,
          longitude: point.lng,
        });
      });
      toast.success('Synced offline location data');
    }
  }, [isConnected, booking.jobId]);

  const requestWakeLock = async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
        console.log('💡 Screen Wake Lock active');
      } catch (err: any) {
        console.warn(`Wake Lock failed: ${err.name}, ${err.message}`);
      }
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('💡 Screen Wake Lock released');
      } catch (err) {
        console.error('Error releasing wake lock', err);
      }
    }
  };

  const stopTracking = React.useCallback(() => {
    if (geolocationIntervalRef.current) {
      clearInterval(geolocationIntervalRef.current);
      geolocationIntervalRef.current = null;
    }
    releaseWakeLock();
    setIsTracking(false);
    setTrackingJobId(null);
    if (socket) socket.emit('leaveJobRoom', booking.jobId);
    toast.info('Stopped live location tracking.');
  }, [booking.jobId, socket, setTrackingJobId]);

  const startTracking = React.useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }

    // Attempt wake lock immediately
    requestWakeLock();

    const sendCurrentLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          if (socket?.connected) {
            // Online: Send immediately
            sendLocationMutation.mutate({
              jobId: booking.jobId,
              latitude: lat,
              longitude: lng,
            });
          } else {
            // Offline: Queue it
            console.log('🔌 Offline: Queuing location point');
            offlineQueueRef.current.push({
              lat,
              lng,
              time: Date.now()
            });
            // Keep queue manageable (last 100 points ~ 15 mins)
            if (offlineQueueRef.current.length > 100) offlineQueueRef.current.shift();
          }
        },
        (error) => {
          if (
            error.code === error.PERMISSION_DENIED ||
            error.code === error.POSITION_UNAVAILABLE
          ) {
            console.error('Geolocation permission denied or unavailable. Stopping tracking.');
            toast.error(`Location access denied. Please enable location permissions to track this job.`);
            stopTracking();
          } else {
            // Temporary error (timeout, etc.) - don't spam console
            console.warn('Temporary geolocation failure, will retry on next interval');
          }
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
      );
    };

    if (!isTracking) {
      // Clear any existing interval just in case
      if (geolocationIntervalRef.current) clearInterval(geolocationIntervalRef.current);

      toast.info('Starting live location tracking ...');
      setIsTracking(true);
      setTrackingJobId(booking.jobId);
      if (socket && socket.connected) {
        socket.emit('joinJobRoom', booking.jobId);
      }

      // Send immediately, then every 10s
      sendCurrentLocation();
      geolocationIntervalRef.current = window.setInterval(sendCurrentLocation, 10000);
    }
  }, [socket, isConnected, booking.jobId, isTracking, sendLocationMutation, setTrackingJobId, stopTracking]);

  // Auto-start tracking when online and in-transit
  // Auto-start tracking when online and in-transit
  useEffect(() => {
    // Prevent "Fighting Jobs" loop: Only auto-start if NO job is currently being tracked.
    if (isConnected && booking.status === BookingStatus.IN_TRANSIT && !isTracking && !trackingJobId) {
      console.log('Auto-starting tracking...');
      startTracking();
    }
  }, [isConnected, booking.status, isTracking, startTracking, trackingJobId]);

  useEffect(() => {
    return () => {
      if (geolocationIntervalRef.current) {
        clearInterval(geolocationIntervalRef.current);
      }
      releaseWakeLock(); // Release lock on unmount
    };
  }, []);

  const getNextAction = () => {
    switch (booking.status as BookingStatus) {
      case BookingStatus.ACCEPTED:
        return { nextStatus: BookingStatus.PICKED_UP, buttonText: 'Confirm Pickup' };
      case BookingStatus.PICKED_UP:
        return { nextStatus: BookingStatus.IN_TRANSIT, buttonText: 'Start Transit' };
      case BookingStatus.IN_TRANSIT:
        return { nextStatus: BookingStatus.AT_DELIVERY, buttonText: 'Arrived at Delivery' };
      case BookingStatus.AT_DELIVERY:
        return { nextStatus: BookingStatus.DELIVERED, buttonText: 'Confirm Delivery' };
      default:
        return null;
    }
  };

  const job = booking.job;
  const nextAction = getNextAction();

  const showTrackingControls = [BookingStatus.IN_TRANSIT].includes(
    booking.status as BookingStatus,
  );

  const isTrackingDisabled = sendLocationMutation.isLoading || !isConnected;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{job.title}</CardTitle>
        <CardDescription>
          Status:{' '}
          <span className="font-semibold text-primary">{booking.status}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Route Information */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <MapPin size={16} className="text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pickup</p>
                <p className="font-medium">{job.pickupDistrict}</p>
                <p className="text-sm text-muted-foreground">{job.pickupAddress}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 pl-2">
              <ArrowRight size={16} className="text-muted-foreground" />
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <MapPin size={16} className="text-red-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Delivery</p>
                <p className="font-medium">{job.deliveryDistrict}</p>
                <p className="text-sm text-muted-foreground">{job.deliveryAddress}</p>
              </div>
            </div>
          </div>

          {/* Job Details */}
          <div className="space-y-2 bg-muted/30 p-3 rounded-md">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Cargo Type:</span>
              <span className="text-sm font-medium">{job.cargoType || 'General'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Weight:</span>
              <span className="text-sm font-medium">{job.weight ? `${job.weight} kg` : 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Your Loads:</span>
              <span className="text-sm font-medium">{booking.bookedLoadCount}</span>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="text-sm font-semibold">Payment:</span>
              <span className="text-sm font-bold text-green-600">MWK {booking.agreedPrice?.toLocaleString() || booking.job.priceAmount?.toLocaleString() || 'TBD'}</span>
            </div>
          </div>

          {/* Contact Details (Prioritize Job-Specific Contacts) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pickup Contact */}
            <div className="space-y-2 bg-blue-50/50 p-3 rounded-md border border-blue-100">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-blue-800 uppercase tracking-wider">Pickup Contact</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Name:</span>
                <span className="text-sm font-medium">{booking.job.pickupContactName || booking.job.client?.profile?.fullName || 'Valued Client'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Phone:</span>
                <a href={`tel:${booking.job.pickupContactPhone || booking.job.client?.phoneOne || booking.job.client?.profile?.phoneOne}`} className="text-sm font-bold text-blue-600 hover:underline">
                  {booking.job.pickupContactPhone || booking.job.client?.phoneOne || booking.job.client?.profile?.phoneOne || 'N/A'}
                </a>
              </div>
            </div>

            {/* Delivery Contact */}
            <div className="space-y-2 bg-green-50/50 p-3 rounded-md border border-green-100">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-green-800 uppercase tracking-wider">Delivery Contact</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Name:</span>
                <span className="text-sm font-medium">{booking.job.deliveryContactName || 'Consignee'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Phone:</span>
                <a href={`tel:${booking.job.deliveryContactPhone}`} className="text-sm font-bold text-green-600 hover:underline">
                  {booking.job.deliveryContactPhone || 'N/A'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 pt-4">
        {showTrackingControls && (
          <div className="flex items-center justify-between sm:justify-start gap-3 w-full sm:w-auto">
            {isTracking ? (
              <Button
                variant="destructive"
                size="lg" // Larger for mobile touch
                onClick={stopTracking}
                disabled={sendLocationMutation.isLoading}
                className="flex-1 sm:flex-none h-12 text-base shadow-lg animate-pulse"
              >
                {sendLocationMutation.isLoading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Square className="h-5 w-5 mr-2" />}
                Stop Tracking
              </Button>
            ) : (
              <Button
                variant="success"
                size="lg" // Larger for mobile touch
                onClick={startTracking}
                disabled={isTrackingDisabled}
                className="flex-1 sm:flex-none h-12 text-base shadow-[0_0_15px_rgba(34,197,94,0.4)]"
              >
                {isTrackingDisabled ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Play className="h-5 w-5 mr-2" />}
                Start Tracking
              </Button>
            )}
            {isTracking && sendLocationMutation.isLoading && (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            )}
            {isConnected ? (
              <Badge className="bg-primary/20 text-primary border-primary/30 flex items-center gap-1 h-9 px-3 backdrop-blur-md">
                <Signal className="h-3 w-3" /> <span className="hidden sm:inline">Live Connection</span>
              </Badge>
            ) : (
              <Badge variant="destructive" className="flex items-center gap-1 h-9 px-3">
                <Terminal className="h-3 w-3" /> Offline
              </Badge>
            )}
          </div>
        )}

        <div className="w-full sm:w-auto sm:flex-grow sm:text-right">
          {nextAction && (
            <Button
              variant="premium"
              size="lg"
              className="w-full sm:w-auto h-12 text-base font-semibold shadow-premium hover:scale-[1.02] transition-transform"
              onClick={() =>
                updateStatusMutation.mutate({
                  bookingId: booking.id,
                  nextStatus: nextAction.nextStatus,
                })
              }
              disabled={updateStatusMutation.isLoading}
            >
              {updateStatusMutation.isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              {nextAction.buttonText}
            </Button>
          )}
          {!nextAction && booking.status === BookingStatus.DELIVERED && (
            <p className="text-sm text-muted-foreground text-center sm:text-right">
              Awaiting client confirmation & payment release.
            </p>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

const TransporterDashboard = () => {
  const { user } = useAuthStore();
  const {
    data: activeBookings,
    isLoading,
    error,
  } = trpc.booking.getMyActiveJobs.useQuery(undefined, {
    enabled: user?.role === 'TRANSPORTER',
    refetchInterval: 30000,
  });

  if (error) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-full">
        <Alert variant="destructive">
          <AlertTitle>Error Loading Jobs</AlertTitle>
          <AlertDescription>{(error as any).message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4">
      <header className="space-y-2 mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold">Transporter Dashboard</h1>
        <p className="text-muted-foreground text-sm sm:text-lg">
          Manage your ongoing jobs and live location tracking.
        </p>
      </header>

      <div className="space-y-4 sm:space-y-6">
        <h2 className="text-lg sm:text-2xl font-bold">Active Jobs</h2>
        {isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}
        {!isLoading && (!activeBookings || activeBookings.length === 0) && (
          <div className="text-center py-8 sm:py-12 border-2 border-dashed rounded-lg">
            <p className="text-muted-foreground text-sm sm:text-base">You have no active jobs.</p>
            <Button asChild className="mt-4" size="sm">
              <Link to="/jobs">Find a Job</Link>
            </Button>
          </div>
        )}
      </div>

      {activeBookings &&
        activeBookings.map((booking: any) => (
          <ActiveJobCard key={booking.id} booking={booking} />
        ))}
    </div>
  );
};

export default TransporterDashboard;
